import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  TrendingUp,
  Users,
  Briefcase,
  DollarSign,
  Activity,
  Target,
  Award,
  Crown,
  Layers,
  RefreshCw,
} from "lucide-react";

const Stat = ({ label, value, sub, icon: Icon, accent }) => (
  <div className="border border-black/10 bg-white p-6" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className={`w-4 h-4 ${accent || "text-[#0033FF]"}`} />
      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
    </div>
    <div className="font-display font-black text-3xl tracking-tighter">{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
  </div>
);

export default function Admin() {
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .get("/admin/me")
      .then((r) => {
        setIsAdmin(!!r.data?.is_admin);
        if (r.data?.is_admin) load();
      })
      .catch(() => setIsAdmin(false))
      .finally(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/stats");
      setStats(r.data);
    } catch (err) {
      toast.error("Could not load admin stats");
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return <div className="font-mono text-sm text-gray-500">Checking access…</div>;
  }

  if (!isAdmin) {
    return (
      <div className="max-w-xl space-y-4" data-testid="admin-no-access">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500">/ Admin</div>
        <h1 className="font-display font-black text-3xl tracking-tighter">Admin access required</h1>
        <p className="text-gray-600 text-sm">
          Your account doesn't have admin privileges. Add your email to the{" "}
          <code className="font-mono text-[12px] bg-[#F8F9FA] border border-black/10 px-1 py-0.5">
            ADMIN_EMAILS
          </code>{" "}
          environment variable, or set <code className="font-mono text-[12px] bg-[#F8F9FA] border border-black/10 px-1 py-0.5">role=admin</code> directly on your user document.
        </p>
      </div>
    );
  }

  const chartMax = Math.max(1, ...((stats?.signups_30d || []).map((d) => d.count)));

  return (
    <div className="space-y-8" data-testid="admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">
            / Admin · Analytics
          </div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
            Maqam command center
          </h1>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="bg-black text-white hover:bg-[#0033FF] font-bold px-5 py-3 flex items-center gap-2 transition-colors disabled:opacity-60"
          data-testid="admin-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {stats && (
        <>
          {/* Revenue row */}
          <div className="grid md:grid-cols-4 gap-4">
            <Stat
              label="MRR"
              value={`SAR ${stats.revenue.mrr_sar.toLocaleString()}`}
              sub={`≈ $${stats.revenue.mrr_usd_est.toLocaleString()}`}
              icon={DollarSign}
              accent="text-[#34C759]"
            />
            <Stat
              label="ARR (projected)"
              value={`SAR ${(stats.revenue.mrr_sar * 12).toLocaleString()}`}
              sub="12× MRR"
              icon={TrendingUp}
              accent="text-[#0033FF]"
            />
            <Stat
              label="All-time revenue"
              value={`SAR ${stats.revenue.revenue_all_time_sar.toLocaleString()}`}
              sub={`${stats.totals.transactions_paid} paid transactions`}
              icon={Activity}
            />
            <Stat
              label="Certs subscribers"
              value={stats.revenue.certs_subscribers}
              sub="SAR 50/mo add-on"
              icon={Award}
              accent="text-[#FF3B30]"
            />
          </div>

          {/* Users row */}
          <div className="grid md:grid-cols-4 gap-4">
            <Stat label="Total users" value={stats.totals.users.toLocaleString()} icon={Users} />
            <Stat label="New 24h" value={stats.totals.users_new_24h} icon={Users} accent="text-[#34C759]" />
            <Stat label="New 7d" value={stats.totals.users_new_7d} icon={Users} accent="text-[#0033FF]" />
            <Stat label="New 30d" value={stats.totals.users_new_30d} icon={Users} />
          </div>

          {/* Signups chart */}
          <div className="border border-black/10 bg-white p-6" data-testid="signups-chart">
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-4">
              Signups · last 30 days
            </div>
            <div className="flex items-end gap-1 h-40">
              {(stats.signups_30d || []).map((d) => (
                <div
                  key={d.day}
                  className="flex-1 bg-[#0033FF] hover:bg-black transition-colors relative group"
                  style={{ height: `${(d.count / chartMax) * 100}%` }}
                  title={`${d.day}: ${d.count}`}
                >
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 opacity-0 group-hover:opacity-100 bg-black text-white text-[10px] px-2 py-1 whitespace-nowrap">
                    {d.day}: {d.count}
                  </div>
                </div>
              ))}
            </div>
            {(stats.signups_30d || []).length === 0 && (
              <div className="text-gray-400 text-sm">No signup data in range yet.</div>
            )}
          </div>

          {/* Plan distribution */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="border border-black/10 bg-white p-6" data-testid="plans-card">
              <div className="flex items-center gap-2 mb-4">
                <Crown className="w-4 h-4 text-[#FF3B30]" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                  Plan distribution
                </div>
              </div>
              <div className="space-y-3">
                {Object.entries(stats.plans).map(([plan, n]) => (
                  <div key={plan} className="flex items-center gap-3">
                    <div className="font-display font-bold w-24 capitalize">{plan}</div>
                    <div className="flex-1 h-2 bg-gray-100 relative">
                      <div
                        className="h-full bg-[#0033FF]"
                        style={{ width: `${Math.min(100, (n / stats.totals.users) * 100)}%` }}
                      />
                    </div>
                    <div className="font-mono text-sm w-10 text-right">{n}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Funnel */}
            <div className="border border-black/10 bg-white p-6" data-testid="funnel-card">
              <div className="flex items-center gap-2 mb-4">
                <Layers className="w-4 h-4 text-[#0033FF]" />
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                  Activation funnel
                </div>
              </div>
              {(() => {
                const f = stats.funnel;
                const steps = [
                  { label: "Signed up", n: f.signed_up },
                  { label: "Profile completed", n: f.profile_completed },
                  { label: "Applied ≥ 1", n: f.applied_at_least_once },
                  { label: "Paying", n: f.paying },
                ];
                const top = Math.max(1, f.signed_up);
                return (
                  <div className="space-y-3">
                    {steps.map((s, i) => {
                      const pct = Math.round((s.n / top) * 100);
                      return (
                        <div key={s.label}>
                          <div className="flex justify-between text-xs font-mono text-gray-600 mb-1">
                            <span>
                              {i + 1}. {s.label}
                            </span>
                            <span>
                              {s.n} · {pct}%
                            </span>
                          </div>
                          <div className="h-3 bg-gray-100 relative">
                            <div className="h-full bg-black" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Ops metrics */}
          <div className="grid md:grid-cols-4 gap-4">
            <Stat label="Live jobs" value={stats.totals.jobs.toLocaleString()} icon={Briefcase} />
            <Stat
              label="Applications"
              value={stats.totals.applications_total.toLocaleString()}
              sub={`${stats.totals.applications_7d} sent in 7d`}
              icon={Target}
            />
            <Stat
              label="Response rate"
              value={`${stats.response_rate_percent}%`}
              sub="Responded / total"
              icon={Activity}
              accent="text-[#34C759]"
            />
            <Stat
              label="Certs access"
              value={stats.revenue.certs_subscribers}
              sub="Paid add-ons"
              icon={Award}
            />
          </div>
        </>
      )}
    </div>
  );
}
